import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import Link from 'next/link';
import * as React from 'react';
import { cn } from '@/code/lib/utils/cn';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

const buttonVariants = cva(
  'relative inline-flex gap-2 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        'airfoil-primary':
          'bg-airfoil-brown-04 text-system-primary-on-primary hover:bg-airfoil-brown-04/90',
        'airfoil-secondary':
          'bg-system-surface-low text-system-text-paragraph hover:bg-system-surface-low/90',
        'airfoil-ghost':
          'bg-transparent text-system-text-subheading hover:text-system-text-paragraph',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10', // button with only icon
        //TODO: to be replaced when tailwind is upgraded
        airfoil: 'rounded-[.625rem] px-4 py-2',
        'airfoil-icon': 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const buttonIconVariants = cva('transition-colors', {
  variants: {
    variant: {
      default: 'text-primary-foreground',
      destructive: 'text-destructive-foreground',
      outline: 'text-primary',
      secondary: 'text-secondary-foreground',
      ghost: '',
      link: 'text-primary',
      'airfoil-primary': 'text-system-primary-container',
      'airfoil-secondary': 'text-system-text-paragraph',
      'airfoil-ghost':
        'text-system-text-subheading group-hover:text-system-text-paragraph',
    },
    size: {
      default: 'size-4',
      sm: 'size-4',
      lg: 'size-5',
      icon: 'size-4',
      airfoil: 'size-4',
      'airfoil-icon': 'size-4',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

export type ButtonVariant = NonNullable<
  Parameters<typeof buttonVariants>[0]
>['variant'];

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  iconLeft?: React.ReactElement;
  iconRight?: React.ReactElement;
  href?: string;
  external?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      iconLeft,
      iconRight,
      href,
      external = false,
      children,
      loading = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    const styledIcon = (icon: React.ReactElement) =>
      React.cloneElement(icon, {
        className: cn(buttonIconVariants({ variant, size })),
      });

    const buttonContent = asChild ? (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    ) : (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {loading ? (
          <Loader2 className="animate-spin" />
        ) : (
          <>
            {iconLeft && styledIcon(iconLeft)}
            {children}
            {iconRight && styledIcon(iconRight)}
          </>
        )}
      </Comp>
    );

    if (href) {
      if (external) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {buttonContent}
          </a>
        );
      }
      return (
        <Link passHref href={href}>
          {buttonContent}
        </Link>
      );
    }
    return buttonContent;
  },
);

Button.displayName = 'ButtonOg';

const ButtonWithDisabledTooltip = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    disabledTooltip?: string;
  }
>(({ disabledTooltip, ...props }, ref) => {
  const buttonContent = <Button ref={ref} {...props} />;

  if (props.disabled && disabledTooltip) {
    return (
      <div className="pt-1">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="block">
                {buttonContent}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{disabledTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return buttonContent;
});
ButtonWithDisabledTooltip.displayName = 'Button';

export { ButtonWithDisabledTooltip as Button, buttonVariants };
